/*
 * TouchKeyboard - System-Tastatur fuer iPad / iPhone
 *
 * Phase 6 der Touch-Optimierung (siehe docs/touch-optimization-specification.md §8):
 * iOS oeffnet die System-Tastatur nur, wenn ein fokussierbares Eingabe-Element im
 * DOM aktiv ist. Unser Editor lebt im Canvas und hat kein eigenes Input-Element —
 * deshalb haengen wir ein verstecktes <textarea> ans body, fokussieren es bei
 * einem Tap auf einen Text-Bereich (6.0a) und uebersetzen die System-Tastatur-
 * Eingaben ueber `beforeinput` in synthetische KeyboardEvents, die durch unsere
 * bestehende `handleKeydown`-Pipeline laufen (6.0b). Damit kriegen wir das
 * komplette Pane-/Field-/Console-Routing kostenlos: kein Routing-Code wird
 * dupliziert.
 *
 * Hardware-Tastatur an iPad bleibt parallel aktiv — iOS sendet ihre Tasten als
 * window.keydown (nicht als beforeinput auf dem Sink), also kein Doppel-Insert.
 */

import { TOUCH } from './config.js'

let _sinkEl = null
let _onSynthKeydown = null  // (eventInit) => void — Callback fuer synth-Keydown
let _isComposing = false    // IME-Composition aktiv (6.0e — Hook bereits hier)

/**
 * Erzeugt das versteckte <textarea> und haengt es an document.body.
 * Idempotent — mehrfacher Aufruf gibt dasselbe Element zurueck.
 *
 * Style-Eigenschaften:
 *  - position: fixed + 1×1 px → liegt nicht im Layout-Fluss, beeinflusst nichts
 *  - opacity: 0 + pointer-events: none → unsichtbar, faengt keine Maus-Events
 *  - autocapitalize/correct/complete/spellcheck off → iOS-Autokorrektur stoert
 *    sonst mit unserem eigenen hunspell-Spellcheck
 *  - Sentinel-Zeichen als Initial-Inhalt → Backspace bei leerem Input feuert auf
 *    iOS kein beforeinput-Event, mit Sentinel schon (wird in 6.0b genutzt)
 *
 * @returns {HTMLTextAreaElement}
 */
export function initTouchKeyboardSink() {
    if (_sinkEl) return _sinkEl

    const el = document.createElement('textarea')
    el.id = TOUCH.keyboardSink.elementId
    el.setAttribute('autocapitalize', 'off')
    el.setAttribute('autocorrect', 'off')
    el.setAttribute('autocomplete', 'off')
    el.setAttribute('spellcheck', 'false')
    el.setAttribute('aria-hidden', 'true')
    el.setAttribute('tabindex', '-1')
    // Inline-Styles, damit kein CSS-Build-Schritt noetig ist
    Object.assign(el.style, {
        position:      'fixed',
        top:           '0',
        left:          '0',
        width:         '1px',
        height:        '1px',
        opacity:       '0',
        pointerEvents: 'none',
        border:        'none',
        outline:       'none',
        background:    'transparent',
        color:         'transparent',
        caretColor:    'transparent',
        resize:        'none',
        zIndex:        '-1'
    })
    el.value = TOUCH.keyboardSink.sentinelChar
    document.body.appendChild(el)
    _sinkEl = el
    return el
}

/**
 * Fokussiert den Sink. Muss SYNCHRON im Touch-User-Gesture-Handler aufgerufen
 * werden — iOS verweigert focus() aus async/setTimeout-Pfaden und blendet die
 * Tastatur dann nicht ein.
 *
 * @returns {boolean} true wenn focus() aufgerufen wurde, false wenn kein Sink
 */
export function focusKeyboardSink() {
    if (!_sinkEl) return false
    // preventScroll: iOS / Mobile-Safari scrollen sonst zum (1×1)-Sink hoch
    _sinkEl.focus({ preventScroll: true })
    return true
}

/**
 * Entzieht dem Sink den Fokus → iOS-Tastatur schliesst.
 *
 * @returns {boolean} true wenn blur() aufgerufen wurde, false wenn kein Sink
 */
export function blurKeyboardSink() {
    if (!_sinkEl) return false
    _sinkEl.blur()
    return true
}

/**
 * Registriert den `beforeinput`-Handler auf dem Sink. Aufrufer reicht eine
 * Callback rein, die einen `eventInit` annimmt und daraus ein synthetisches
 * KeyboardEvent baut, das er durch die normale Keydown-Pipeline jagt.
 *
 * Mapping (Spec §8 "Eingabe-Pfad"):
 *  - insertText                                → key=Buchstabe (pro Code-Point)
 *  - insertParagraph / insertLineBreak         → key='Enter'
 *  - deleteContentBackward (+ Word/Soft-Line)  → key='Backspace'
 *  - deleteContentForward  (+ Word/Soft-Line)  → key='Delete'
 *  - historyUndo                               → meta+ctrl + key='z'
 *  - historyRedo                               → meta+ctrl+shift + key='z'
 *
 * insertFromPaste / insertFromDrop bleiben Out-of-Scope fuer 6.0b — Paste hat
 * eigenen Pfad ueber das clipboard-Event und braucht event.dataTransfer.
 *
 * Idempotent: zweiter Aufruf ersetzt den Callback (keine doppelte Listener-
 * Registrierung). Nutzbar fuer Hot-Reload / Tests.
 *
 * @param {(eventInit: KeyboardEventInit) => void} onSynthKeydown
 */
export function installBeforeInputHandler(onSynthKeydown) {
    if (!_sinkEl) initTouchKeyboardSink()
    const firstInstall = (_onSynthKeydown === null)
    _onSynthKeydown = onSynthKeydown
    if (firstInstall) {
        _sinkEl.addEventListener('beforeinput', _handleBeforeInput)
        _sinkEl.addEventListener('input', _handleInput)
    }
}

function _handleBeforeInput(event) {
    // IME (6.0e): waehrend Composition keine eigene Uebersetzung — der finale
    // Text kommt am `compositionend` an. Default-Verhalten durchlassen, damit
    // die Composition-Engine ihre Pre-Edit-Darstellung machen kann.
    if (_isComposing) return

    const data = event.data || ''

    switch (event.inputType) {
        case 'insertText':
            // Pro Code-Point ein synthetisches keydown — Surrogate-Paare und
            // Emojis bleiben atomar dank `for..of` ueber den String.
            for (const ch of data) {
                _dispatchSynth({ key: ch })
            }
            break
        case 'insertParagraph':
        case 'insertLineBreak':
            _dispatchSynth({ key: 'Enter', code: 'Enter' })
            break
        case 'deleteContentBackward':
        case 'deleteWordBackward':
        case 'deleteSoftLineBackward':
        case 'deleteHardLineBackward':
            _dispatchSynth({ key: 'Backspace', code: 'Backspace' })
            break
        case 'deleteContentForward':
        case 'deleteWordForward':
        case 'deleteSoftLineForward':
        case 'deleteHardLineForward':
            _dispatchSynth({ key: 'Delete', code: 'Delete' })
            break
        case 'historyUndo':
            // metaKey UND ctrlKey, damit Mac- und Win-Detection beide passen
            _dispatchSynth({ key: 'z', code: 'KeyZ', metaKey: true, ctrlKey: true })
            break
        case 'historyRedo':
            _dispatchSynth({ key: 'z', code: 'KeyZ', metaKey: true, ctrlKey: true, shiftKey: true })
            break
        default:
            // Unbekannter / nicht unterstuetzter Input-Type (z.B. insertFromPaste
            // in 6.0b) — Sink-Inhalt waechst trotzdem, _handleInput resettet
            // ihn gleich darauf.
            break
    }
}

function _handleInput() {
    // Bei IME: nichts tun — die Composition haelt den Sink-Inhalt fuer den
    // Pre-Edit-Zustand bereit.
    if (_isComposing) return
    if (!_sinkEl) return
    // Direkte Property-Zuweisung loest weder beforeinput noch input aus → kein
    // Echo-Loop. Sentinel bleibt damit immer im Sink, deleteContentBackward
    // feuert weiterhin bei jedem Backspace (sonst wuerde iOS bei leerem Input
    // schweigen).
    _sinkEl.value = TOUCH.keyboardSink.sentinelChar
    try {
        _sinkEl.setSelectionRange(1, 1)
    } catch (_) {
        // jsdom kann bei nicht-fokussiertem Element werfen — egal
    }
}

function _dispatchSynth(eventInit) {
    if (!_onSynthKeydown) return
    _onSynthKeydown(eventInit)
}

/**
 * Liefert das Sink-Element (oder null). Nur fuer Tests / Diagnose, im normalen
 * Code-Fluss soll die Kapselung erhalten bleiben.
 */
export function _getKeyboardSinkElement() {
    return _sinkEl
}

/**
 * Setzt das Modul fuer Tests zurueck — entfernt das Sink-Element aus dem DOM
 * und vergisst die Referenz. Nicht aus Produktiv-Code aufrufen.
 */
export function _resetTouchKeyboardForTest() {
    if (_sinkEl) {
        _sinkEl.removeEventListener('beforeinput', _handleBeforeInput)
        _sinkEl.removeEventListener('input', _handleInput)
        if (_sinkEl.parentNode) _sinkEl.parentNode.removeChild(_sinkEl)
    }
    _sinkEl = null
    _onSynthKeydown = null
    _isComposing = false
}
